import { FC, useState } from 'react';
import { Alert, Dialog, DialogContent, DialogTitle, IconButton, Snackbar } from '@mui/material';
import { Close } from '@mui/icons-material';
import {
    useAiExtractFromImage,
    useAiGenerateFromList,
    useAiGenerateFromTopic,
    useBulkCreateWords,
} from '../../hooks/useWord';
import { AdminVocabItem, AiTopicDifficulty } from '../../models/word/aiVocabModels';
import { AiMethod, StepMethodSelect } from './ai-add/StepMethodSelect';
import { StepWordListInput } from './ai-add/StepWordListInput';
import { StepTopicInput } from './ai-add/StepTopicInput';
import { StepImageInput } from './ai-add/StepImageInput';
import { StepLoading } from './ai-add/StepLoading';
import { ReviewRow, StepReview } from './ai-add/StepReview';

interface Props {
    open: boolean;
    onClose: () => void;
    wordbookId: number;
    wordLang: string;
    meaningLang: string;
    existingWordTexts: string[];
}

type StepKey = 'method' | 'wordlist' | 'topic' | 'image' | 'loading' | 'review';

const TITLES: Record<StepKey, string> = {
    method: 'AI로 단어 추가',
    wordlist: '단어목록 입력',
    topic: '주제 입력',
    image: '이미지 업로드',
    loading: 'AI 생성 중',
    review: '검토 및 저장',
};

const LOADING_MESSAGES: Record<AiMethod, string> = {
    wordlist: 'AI가 단어의 뜻을 생성하고 있습니다…',
    topic: 'AI가 주제와 관련된 단어를 생성하고 있습니다…',
    image: 'AI가 이미지에서 단어를 추출하고 있습니다…',
};

function normalize(text: string): string {
    return text.toLowerCase().trim();
}

function toRows(items: AdminVocabItem[], existing: Set<string>): ReviewRow[] {
    return items.map((item, idx) => {
        const dup = existing.has(normalize(item.word));
        return {
            id: `${idx}-${item.word}-${Math.random().toString(36).slice(2, 8)}`,
            wordText: item.word,
            meaningText: item.meaning,
            difficultyBase: typeof item.difficultyScore === 'number' ? item.difficultyScore : 50,
            dictId: item.dictId ?? null,
            pos: item.pos ?? null,
            phonetic: item.phonetic ?? null,
            selected: !dup,
            duplicateOfExisting: dup,
        };
    });
}

export const AiAddWordsDialog: FC<Props> = ({
    open,
    onClose,
    wordbookId,
    wordLang,
    meaningLang,
    existingWordTexts,
}) => {
    const [step, setStep] = useState<StepKey>('method');
    const [method, setMethod] = useState<AiMethod | null>(null);
    const [rows, setRows] = useState<ReviewRow[]>([]);
    const [emptyResult, setEmptyResult] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    const aiList = useAiGenerateFromList();
    const aiTopic = useAiGenerateFromTopic();
    const aiImage = useAiExtractFromImage();
    const bulkCreate = useBulkCreateWords();

    const existingSet = new Set(existingWordTexts.map(normalize));

    const reset = () => {
        setStep('method');
        setMethod(null);
        setRows([]);
        setEmptyResult(false);
        setErrorMessage(null);
    };

    const handleClose = () => {
        if (bulkCreate.isPending) return;
        reset();
        onClose();
    };

    const handleMethodSelect = (m: AiMethod) => {
        setMethod(m);
        setEmptyResult(false);
        setErrorMessage(null);
        setStep(m === 'wordlist' ? 'wordlist' : m === 'topic' ? 'topic' : 'image');
    };

    const handleGenerated = (items: AdminVocabItem[], fallbackStep: StepKey) => {
        if (items.length === 0) {
            setEmptyResult(true);
            setStep(fallbackStep);
            return;
        }
        setRows(toRows(items, existingSet));
        setStep('review');
    };

    const handleError = (e: unknown, fallbackStep: StepKey) => {
        const msg = e instanceof Error ? e.message : 'AI 생성에 실패했습니다.';
        setErrorMessage(msg);
        setStep(fallbackStep);
    };

    const submitWordList = (words: string[]) => {
        setStep('loading');
        aiList.mutate(
            { wordbookId, words, wordLang, meaningLang },
            {
                onSuccess: (res) => handleGenerated(res.words, 'wordlist'),
                onError: (e) => handleError(e, 'wordlist'),
            },
        );
    };

    const submitTopic = (params: { topic: string; wordCount: number; difficulty: AiTopicDifficulty }) => {
        setStep('loading');
        aiTopic.mutate(
            { wordbookId, wordLang, meaningLang, ...params },
            {
                onSuccess: (res) => handleGenerated(res.words, 'topic'),
                onError: (e) => handleError(e, 'topic'),
            },
        );
    };

    const submitImage = (file: File) => {
        setStep('loading');
        aiImage.mutate(
            { wordbookId, image: file, wordLang, meaningLang },
            {
                onSuccess: (res) => handleGenerated(res.words, 'image'),
                onError: (e) => handleError(e, 'image'),
            },
        );
    };

    const handleSave = () => {
        const selected = rows.filter((r) => r.selected);
        if (selected.length === 0) return;
        bulkCreate.mutate(
            {
                wordbookId,
                checkDuplicate: true,
                words: selected.map((r) => ({
                    wordbookId,
                    wordLang,
                    wordText: r.wordText,
                    meaningLang,
                    meaningText: r.meaningText,
                    difficultyBase: r.difficultyBase,
                    dictId: r.dictId,
                    checkDuplicate: true,
                })),
            },
            {
                onSuccess: (res) => {
                    setSuccessMessage(
                        `단어 ${res.successCount}개 추가 완료 (실패 ${res.failedCount}개)`,
                    );
                    reset();
                    onClose();
                },
                onError: (e) => {
                    setErrorMessage(e instanceof Error ? e.message : '저장에 실패했습니다.');
                },
            },
        );
    };

    return (
        <>
            <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    {TITLES[step]}
                    <IconButton onClick={handleClose} disabled={bulkCreate.isPending}>
                        <Close />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    {emptyResult && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            추출된 단어가 없습니다. 다시 시도해주세요.
                        </Alert>
                    )}
                    {errorMessage && (
                        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage(null)}>
                            {errorMessage}
                        </Alert>
                    )}
                    {step === 'method' && <StepMethodSelect onSelect={handleMethodSelect} />}
                    {step === 'wordlist' && (
                        <StepWordListInput onBack={reset} onSubmit={submitWordList} />
                    )}
                    {step === 'topic' && <StepTopicInput onBack={reset} onSubmit={submitTopic} />}
                    {step === 'image' && <StepImageInput onBack={reset} onSubmit={submitImage} />}
                    {step === 'loading' && method && <StepLoading message={LOADING_MESSAGES[method]} />}
                    {step === 'review' && (
                        <StepReview
                            rows={rows}
                            onChange={setRows}
                            onRestart={reset}
                            onCancel={handleClose}
                            onSave={handleSave}
                            saving={bulkCreate.isPending}
                        />
                    )}
                </DialogContent>
            </Dialog>
            <Snackbar
                open={!!successMessage}
                autoHideDuration={3500}
                onClose={() => setSuccessMessage(null)}
                message={successMessage}
            />
        </>
    );
};
